import { useTranslation } from "@redotech/redo-i18n";
import { InteractiveMapLocation } from "@redotech/redo-model/draft-return/draft-return";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { LocationsMapModalBody } from "@redotech/redo-web/arbiter-components/interactive-map/interactive-map";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { useQuery } from "@tanstack/react-query";
import { memo, useState } from "react";

export interface DropoffLocationsSearchParams {
  draftReturnId?: string;
  returnId?: string;
}

export const DropoffLocationsButton = memo(function DropoffLocationsButton({
  draftReturnId,
  returnId,
  customerCountry,
  autoOpen = false,
  modalTitle,
  modalSubtitle,
  searchDropoffLocations,
}: {
  draftReturnId?: string;
  returnId?: string;
  customerCountry?: string;
  autoOpen?: boolean;
  modalTitle?: string;
  modalSubtitle?: string;
  searchDropoffLocations: (
    params: DropoffLocationsSearchParams,
  ) => Promise<InteractiveMapLocation[]>;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(autoOpen);
  const query = useQuery({
    queryKey: ["dropoffLocations", draftReturnId, returnId],
    queryFn: () => searchDropoffLocations({ draftReturnId, returnId }),
    enabled: isOpen,
    staleTime: Infinity,
  });

  return (
    <Flex>
      <RedoButton
        hierarchy="secondary"
        onClick={(e) => {
          e.preventDefault();
          setIsOpen(true);
        }}
        text={t("See all locations")}
      />
      <RedoModal
        isOpen={isOpen}
        onModalCloseRequested={() => setIsOpen(false)}
        subtitle={modalSubtitle}
        title={modalTitle ?? t("Find drop-off locations")}
      >
        <LocationsMapModalBody
          customerCountry={customerCountry}
          error={!!query.error}
          errorText={t("Error loading drop-off locations")}
          locations={query.data ?? null}
          pending={query.isPending && query.fetchStatus !== "idle"}
        />
      </RedoModal>
    </Flex>
  );
});
